/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import {
  Box,
  FlexContainer,
  PieChart,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";

type Props = {
  progress: number;
  color?: "danger" | "notification" | "success" | "warning";
  size?: number;
  strokeWidth?: number;
  isForecast?: boolean;
  hasBudget?: boolean;
  icon?: "application" | "cloud";
};

export function BudgetChart({
  progress,
  color,
  isForecast = false,
  size = 144,
  strokeWidth = 20,
  hasBudget = true,
  icon = "application",
}: Props) {
  const chartColor = hasBudget ? color : "notification";
  const chartProgress = hasBudget ? progress : 0;
  const isFullSize = size > 120;

  return (
    <FlexContainer
      justifyContent="center"
      alignItems="center"
      css={{ height: "100%" }}
    >
      <PieChart
        progress={chartProgress}
        size={size}
        strokeWidth={strokeWidth}
        color={chartColor}
      >
        <If condition={hasBudget}>
          <Then>
            <PieChart
              progress={progress}
              size={size}
              strokeWidth={strokeWidth}
              color={color}
            >
              <Text
                className="chartValue"
                size={isFullSize ? "lg" : "sm"}
                tag="span"
                weight="medium"
              >
                {progress.toFixed(1)}%
              </Text>
              <If condition={isFullSize}>
                <Then>
                  <Text
                    className="chartCaption"
                    size="xs"
                    tag="span"
                    align="center"
                  >
                    <If condition={isForecast}>
                      <Then>
                        forecasted
                        <br /> budget usage
                      </Then>
                      <Else>budget used</Else>
                    </If>
                  </Text>
                </Then>
              </If>
            </PieChart>
          </Then>
          <Else>
            <SVGIcon
              name={icon}
              color="notification"
              size={isFullSize ? "xl" : "md"}
            />
          </Else>
        </If>
      </PieChart>
    </FlexContainer>
  );
}

type BudgetChartWrapperProps = {
  children: React.ReactNode;
};

export function BudgetChartWrapper({ children }: BudgetChartWrapperProps) {
  return (
    <div
      css={{
        marginRight: theme.spacing.spacing05,
        height: "100%",
        display: "flex",
      }}
    >
      <Box boxStyle="lightGrey">{children}</Box>
    </div>
  );
}
